import { useMemo, useState } from 'react';
import { ArrowBack, ArrowForward, LocationOn, Shield } from '@mui/icons-material';
import { toast } from 'sonner';
import { DSCommunitySchema } from '../../data/types';
import { usStates, USStatesEnum } from '../../utils/enums';
import {
  hasCompleteComplianceLocation,
  markComplianceOnboardingComplete,
  resolveComplianceLocation,
  toCommunityLocationUpdate,
} from '../../utils/complianceHelpers';
import { useLoadCommunity } from '../../hooks/authhooks';
import ComplianceChecklist from './ComplianceChecklist';

type OnboardingWizardProps = {
  community: DSCommunitySchema;
  communityId: string;
  onComplete: () => void;
  onSkip?: () => void;
};

/** First-time wizard when community location data is missing for compliance generation. */
export const OnboardingWizard = ({
  community,
  communityId,
  onComplete,
  onSkip,
}: OnboardingWizardProps) => {
  const [step, setStep] = useState<'location' | 'preview'>('location');
  const [form, setForm] = useState(() => resolveComplianceLocation(community));
  const { updateCommunityDetails, isUpdatePending } = useLoadCommunity(communityId);

  const previewItems = useMemo(
    () => [
      {
        id: 'preview-food',
        item_key: 'food_facility_permit',
        resource_id: '',
        resource_type: 'kitchen' as const,
        title: 'Food facility / establishment license',
        description:
          'Register the kitchen with the local health department and keep a valid food facility or commissary permit on file.',
        status: 'pending' as const,
        legal: {
          summary:
            'Shared kitchens must document valid food-establishment licensing before accepting bookings.',
          regionNote: `Requirements in ${form.city || 'your city'}, ${form.state || 'your state'} vary by municipality.`,
          references: [],
        },
      },
      {
        id: 'preview-co',
        item_key: 'host_certificate_of_occupancy',
        resource_id: '',
        resource_type: 'kitchen' as const,
        title: 'Host Certificate of Occupancy',
        description:
          'Collect the current Certificate of Occupancy or equivalent approved-use record for the host facility.',
        status: 'pending' as const,
        legal: {
          summary:
            'An existing kitchen is not automatically pilot-ready. Capture the current CO and approved use for the host space.',
          regionNote:
            form.state?.toLowerCase().includes('va') || form.city?.toLowerCase() === 'arlington'
              ? 'Arlington partners should keep Environmental Health, Inspection Services Building Code, and Zoning as separate lanes.'
              : 'Confirm occupancy and use authorization with local building and zoning agencies.',
          references: [],
        },
      },
      {
        id: 'preview-insurance',
        item_key: 'liability_insurance',
        resource_id: '',
        resource_type: 'kitchen' as const,
        title: 'Liability insurance',
        description:
          'Maintain general liability coverage that includes commercial kitchen use and tenant activities.',
        status: 'pending' as const,
        legal: {
          summary:
            'Commercial kitchen hosts need liability insurance that covers shared use by food entrepreneurs.',
          regionNote: 'Confirm policy language covers multiple independent operators where applicable.',
          references: [],
        },
      },
    ],
    [form.city, form.state]
  );

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleSaveLocation = async () => {
    if (!hasCompleteComplianceLocation(undefined, {
      city: form.city,
      county: form.county,
      state: form.state,
      zipcode: form.zipcode,
    })) {
      toast.error('Please enter city, state, and zip code to continue.');
      return;
    }

    await updateCommunityDetails(communityId, toCommunityLocationUpdate(form));
    setStep('preview');
  };

  const handleFinish = () => {
    markComplianceOnboardingComplete(communityId);
    onComplete();
  };

  return (
    <div className="mx-auto w-11/12 max-w-3xl py-8">
      <div className="mb-6 rounded-xl border border-[#E9E9E9] bg-white p-6">
        <p className="font-raleway text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">
          Setup checklist onboarding
        </p>
        <h2 className="mt-2 font-raleway text-2xl font-bold text-[#013847]">
          {step === 'location' ? 'Confirm your location' : 'Review your requirements'}
        </h2>
        <p className="mt-2 font-open-sans text-sm text-[#4D4D4D]">
          We use city, county, state, and zip code to generate regulatory screening items. HomeCook
          organizes records - it does not issue permits, licenses, or Certificates of Occupancy.
        </p>
      </div>

      {step === 'location' ? (
        <div className="rounded-xl border border-[#E9E9E9] bg-white p-6">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EFF8FA]">
              <LocationOn className="text-primary-400" />
            </span>
            <div>
              <p className="font-raleway text-base font-semibold text-[#013847]">Location details</p>
              <p className="font-open-sans text-sm text-[#808080]">
                Used to tailor kitchen compliance items for your jurisdiction.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="font-open-sans text-sm text-[#808080]">City</span>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                className="h-[50px] rounded-md border border-[#E9E9E9] px-3 font-open-sans text-sm"
                placeholder="Arlington"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="font-open-sans text-sm text-[#808080]">County</span>
              <input
                name="county"
                value={form.county}
                onChange={handleChange}
                className="h-[50px] rounded-md border border-[#E9E9E9] px-3 font-open-sans text-sm"
                placeholder="Arlington County"
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="font-open-sans text-sm text-[#808080]">State</span>
              <select
                name="state"
                value={form.state}
                onChange={handleChange}
                className="h-[50px] rounded-md border border-[#E9E9E9] px-3 font-open-sans text-sm"
              >
                <option value="">-- Select State --</option>
                {usStates.map((state) => (
                  <option value={state} key={state}>
                    {USStatesEnum[state]?.fullName}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="font-open-sans text-sm text-[#808080]">Zip code</span>
              <input
                name="zipcode"
                value={form.zipcode}
                onChange={handleChange}
                className="h-[50px] rounded-md border border-[#E9E9E9] px-3 font-open-sans text-sm"
                placeholder="22201"
              />
            </label>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            {onSkip && (
              <button
                type="button"
                onClick={onSkip}
                className="font-open-sans text-sm text-[#808080] underline"
              >
                Skip for now
              </button>
            )}
            <button
              type="button"
              disabled={isUpdatePending}
              onClick={handleSaveLocation}
              className="ml-auto inline-flex h-11 items-center gap-2 rounded-lg bg-primary-200 px-5 font-open-sans text-sm font-medium text-white disabled:opacity-60"
            >
              Continue
              <ArrowForward sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex gap-3 rounded-xl border border-[#5DB6CE]/20 bg-[#EFF8FA]/60 p-4">
            <Shield className="mt-0.5 shrink-0 text-primary-400" />
            <p className="font-open-sans text-sm text-[#013847]/80">
              After you create a kitchen resource, HomeCook will generate the full checklist for{' '}
              {[form.city, form.state, form.zipcode].filter(Boolean).join(', ')}. Upload documents
              on the Setup checklist tab as you collect them.
            </p>
          </div>

          <ComplianceChecklist
            regionNote={`Preview for ${form.city}, ${form.state}. Final items may include Arlington Inspection Services Building Code screening when applicable.`}
            items={previewItems}
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setStep('location')}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#E9E9E9] px-4 font-open-sans text-sm text-[#4D4D4D]"
            >
              <ArrowBack sx={{ fontSize: 18 }} />
              Back
            </button>
            <button
              type="button"
              onClick={handleFinish}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary-200 px-5 font-open-sans text-sm font-medium text-white"
            >
              Open setup checklist
              <ArrowForward sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingWizard;
